import React, { memo, useCallback, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';

import useClick from '../../../hooks/useClick';
import {
  LocationIcon,
  LockedIcon,
  ParcelIcon,
  DotsOptionsIcon,
} from '../../icons';
import { View, Text } from '../../Base';
import { defaultRoutesShortcuts } from '../../../constants/strings';
import defaultRoutes from '../../../enums/defaultRoutes';
import PopoverMenu, { LockMenuItems } from './RouteComponents/PopoverMenu';
import palette from '../../styles/palette';

const useStyles = makeStyles(({ typography }) => ({
  section: {
    padding: '0.625rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: typography.pxToRem(18),
  },
  active: {
    background: palette.liteGreen,
  },
  title: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  routeName: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 'bold',
    marginBottom: '0.250rem',
  },
  routeCode: {
    width: '2.5rem',
    height: '1.25rem',
    lineHeight: '1.25rem',
    fontSize: '0.875rem',
    textAlign: 'center',
    background: palette.slate,
    color: 'white',
    borderRadius: '0.125rem',
    fontWeight: 'bold',
  },
  right: {
    marginLeft: '0.313rem',
  },
  details: {
    display: 'flex',
    flexDirection: 'row',
  },
  icon: {
    marginLeft: '0.274rem',
    marginRight: '1.563rem',
    height: 0,

    '& svg': {
      maxHeight: '0.75rem',
    },
  },
  light: {
    color: 'white',

    '& svg': {
      fill: palette.typography.white.medium,
    },
  },
  dark: {
    color: 'black',

    '& svg': {
      fill: palette.typography.black.medium,
    },
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  info: {
    display: 'flex',
    flexDirection: 'row',
  },
}));

const RouteDefaultsCard = ({
  onDoubleClick = () => {},
  onClick = () => {},
  route,
  active,
  showLockRoute,
  onLockChange,
  onOpenLockRoutePopover,
}) => {
  const styles = useStyles();
  const { routeCode, totalStops, totalParcels, routeLockedBy } = route;
  const click = useClick(onClick, onDoubleClick);
  const isNCD = routeCode === defaultRoutesShortcuts.NCD;
  const { routeName, background, variant } = useMemo(
    () => defaultRoutes[routeCode],
    [routeCode]
  );

  const renderPopover = useCallback(
    ({ handleMenuItemClick, setOpen }) => (
      <LockMenuItems
        onItemClick={handleMenuItemClick}
        setOpen={setOpen}
        routeLockedBy={!!routeLockedBy}
      />
    ),
    [routeLockedBy]
  );

  return (
    <View
      onClick={click}
      className={clsx(styles.section, active && styles.active)}
      style={!active ? { background } : {}}
    >
      <View className={styles.info}>
        <View className={styles.left}>
          <Text className={styles.routeCode} data-testid="route-code">
            {routeCode}
          </Text>
          {showLockRoute && isNCD && !!routeLockedBy && (
            <View data-testid="default-route-lock-status">
              <LockedIcon fill={palette.typography.white.solid} />
            </View>
          )}
        </View>
        <View className={styles.right}>
          <Text
            className={clsx(styles.routeName, styles[variant])}
            data-testid="route-name"
          >
            {routeName}
          </Text>
          <View className={clsx(styles.details, styles[variant])}>
            <Text className={styles.title} data-testid="route-stops">
              {`${totalStops}`.padStart(3, 0)}
            </Text>
            <View className={clsx(styles.icon, styles[variant])}>
              <LocationIcon />
            </View>
            <Text
              className={clsx(styles.title, styles[variant])}
              data-testid="route-parcels"
            >
              {`${totalParcels}`.padStart(3, 0)}
            </Text>
            <View className={clsx(styles.icon, styles[variant])}>
              <ParcelIcon />
            </View>
          </View>
        </View>
      </View>
      {showLockRoute && isNCD && (
        <View className={styles.right} data-testid="lock-default-route">
          <PopoverMenu
            icon={<DotsOptionsIcon />}
            onChange={onLockChange}
            placement="left-start"
            render={renderPopover}
            onOpenPopover={onOpenLockRoutePopover}
          />
        </View>
      )}
    </View>
  );
};

export default memo(RouteDefaultsCard);
